import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Button,
  Typography,
  Avatar,
  Stack,
} from '@mui/material';
import { useDictionary, useIsDesktop, useMedications } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useEffect, useState } from 'react';
import { IMedication } from 'API';
import { goToRenewPrescription, goToRequestReferral, isExpired } from 'Utils';
import { ExpiredMedications } from './ExpiredMedications';
import { AccordionArrowBlue, PurpleWarning, UploadFileBlue } from 'Images';
import { isBefore } from 'date-fns';
import { sendEvent } from 'Utils/sendEvent';
import { NextPrevButton } from 'Components';
export const RenewPrescriptionCollapse = () => {
  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.MedicationsAndPrescriptionsLobby
  );

  const isDesktop = useIsDesktop();
  const { medications } = useMedications();
  const [expiredMedications, setExpiredMedications] = useState<IMedication[]>(
    []
  );
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  useEffect(() => {
    setExpiredMedications(
      medications
        .filter((medication) => isExpired(new Date(medication.nextrefreshdate)))
        .sort((a, b) => {
          if (
            isBefore(new Date(a.nextrefreshdate), new Date(b.nextrefreshdate))
          ) {
            return 1;
          } else {
            return -1;
          }
        })
    );
  }, [medications]);

  if (expiredMedications.length === 0) return null;

  const buttonStyle = {
    width: isDesktop ? '300px' : '235px',
    backgroundColor: 'white',
    boxShadow: '0px 2px 16px rgba(4, 16, 46, 0.08)',
    borderRadius: '100px',
    borderColor: 'primary.main',
    gap: '3px',
    padding: isDesktop ? '11px 24px' : '7px 21px',
    ':hover': {
      background: '#d1cdcd',
      cursor: 'pointer',
    },
  };

  return (
    <Stack
      sx={{
        backgroundColor: 'purple2.shade_100',
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'purple2.shade_200',
        padding: isDesktop ? '12px 24px' : '16px',
        alignItems: 'center',
        mt: 4,
      }}
    >
      <Accordion
        expanded={isExpanded}
        square
        elevation={0}
        onChange={(_e, expanded) => {
          setIsExpanded(expanded ? true : false);
          sendEvent('Medicine', 'ExpandExpiredMedicine');
        }}
        sx={{ width: '100%' }}
      >
        <AccordionSummary
          aria-controls={'accordion-1'}
          id={'accordion-1'}
          sx={{
            backgroundColor: 'purple2.shade_100',
            padding: '0',
            '& .MuiAccordionSummary-expandIconWrapper': {
              alignSelf: 'start',
              mt: isExpanded ? '20px' : '12px',
            },
          }}
          expandIcon={
            isExpanded ? (
              <Stack flexDirection={'row'}>
                <Avatar
                  sx={{
                    height: '24px',
                    width: '24px',
                  }}
                  alt={''}
                  src={AccordionArrowBlue}
                />
              </Stack>
            ) : (
              <Stack flexDirection={'row'}>
                <Avatar
                  sx={{
                    height: '24px',
                    width: '24px',
                  }}
                  alt={''}
                  src={AccordionArrowBlue}
                />
              </Stack>
            )
          }
        >
          <Stack gap='12px'>
            <Stack flexDirection='row' alignItems='center' gap='6px'>
              {isDesktop && (
                <Avatar
                  src={PurpleWarning}
                  alt=''
                  sx={{
                    display: 'inline-block',
                    height: '24px',
                    width: '24px',
                  }}
                />
              )}
              <Typography component={'h3'} variant='body2'>
                {isExpanded
                  ? dictionary.thereAreNumberOfMedicationsThatRequiresYourAttention(
                      expiredMedications.length
                    )
                  : dictionary.thereAreNumberOfMedicationsThatRequiresYourAttentionHeader(
                      expiredMedications.length
                    )}
              </Typography>
            </Stack>
            {isExpanded && (
              <>
                <Typography
                  variant={isDesktop ? 'body3' : 'body6'}
                  pl={isDesktop ? 3.5 : 0}
                  width={isDesktop ? undefined : '130%'}
                >
                  {
                    dictionary.attachingDocumentsThatAreRelevantWillHelpUsTakeCareOfYourRequestsFaster
                  }
                </Typography>
              </>
            )}
          </Stack>
          <Typography variant='body2' color='primary.main'>
            {isExpanded ? dictionary.toHide : dictionary.toShow}
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            backgroundColor: '#fff',
            padding: '24px',
            borderRadius: '12px',
          }}
          role='table'
        >
          <ExpiredMedications expiredMedications={expiredMedications} />

          <Grid
            container
            spacing={4}
            sx={{
              padding: isDesktop ? '24px 0' : '24px 0 0',
            }}
            justifyContent={isDesktop ? 'right' : 'center'}
          >
            <Grid
              item
              xs={isDesktop ? undefined : 12}
              textAlign={isDesktop ? undefined : 'center'}
            >
              <Button
                onClick={() => goToRenewPrescription()}
                variant='outlined'
                sx={{ ...buttonStyle }}
                startIcon={
                  <Avatar
                    alt=''
                    src={UploadFileBlue}
                    sx={{ width: '20px', height: '20px' }}
                  />
                }
              >
                <Typography variant='body4'>
                  {dictionary.successOrderLeftButton}
                </Typography>
              </Button>
            </Grid>
            <Grid item xs={isDesktop ? undefined : 12}>
              <NextPrevButton
                text={dictionary.referralRequest}
                callback={goToRequestReferral}
                isNext
                style={isDesktop ? undefined : { button: { m: '0 auto' } }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};
